export const organizationPlanIds = ["community", "grow", "impact", "network"] as const;
export type OrganizationPlanId = (typeof organizationPlanIds)[number];

export const organizationPlanStatuses = ["active", "archived"] as const;
export type OrganizationPlanStatus = (typeof organizationPlanStatuses)[number];

export const organizationSubscriptionStatuses = [
  "free",
  "trialing",
  "active",
  "pastDue",
  "restricted",
  "cancelled",
] as const;
export type OrganizationSubscriptionStatus = (typeof organizationSubscriptionStatuses)[number];

export const organizationTrialStatuses = ["none", "eligible", "active", "expired"] as const;
export type OrganizationTrialStatus = (typeof organizationTrialStatuses)[number];

export const foundingOrganizationStatuses = ["none", "active", "removed"] as const;
export type FoundingOrganizationStatus = (typeof foundingOrganizationStatuses)[number];

export const organizationEntitlementIds = [
  "community.core",
  "communications.basic",
  "communications.advanced",
  "events.core",
  "events.paid",
  "events.advancedRegistration",
  "events.qrCheckIn",
  "events.exports",
  "commerce.donations",
  "commerce.campaigns",
  "ramadan.operations",
  "operations.modes",
  "spaces.core",
  "volunteers.core",
  "directory.management",
  "analytics.advanced",
  "ai.cloud",
  "network.multiSite",
] as const;
export type OrganizationEntitlementId = (typeof organizationEntitlementIds)[number];

export const entitlementOverrideModes = ["inherit", "forceEnabled", "forceDisabled"] as const;
export type EntitlementOverrideMode = (typeof entitlementOverrideModes)[number];

export const usageLimitStatuses = ["underLimit", "overLimit", "restricted"] as const;
export type UsageLimitStatus = (typeof usageLimitStatuses)[number];

export const platformFeePolicyStatuses = ["active", "archived"] as const;
export type PlatformFeePolicyStatus = (typeof platformFeePolicyStatuses)[number];

export const platformFeeTypes = ["none", "basisPoints", "fixed", "basisPointsPlusFixed"] as const;
export type PlatformFeeType = (typeof platformFeeTypes)[number];

export const platformFeeTransactionTypes = [
  "donation",
  "spaceRental",
  "ramadanSponsorship",
] as const;
export type PlatformFeeTransactionType = (typeof platformFeeTransactionTypes)[number];

export const commerceComponents = [
  "generalDonation",
  "campaignDonation",
  "iftarSponsorship",
  "spaceBooking",
] as const;
export type CommerceComponent = (typeof commerceComponents)[number];

export const platformFeeCollectionModes = ["addOnToPayer", "deductFromOrg"] as const;
export type PlatformFeeCollectionMode = (typeof platformFeeCollectionModes)[number];

export const platformFeeRuleScopes = [
  "platformFallback",
  "componentDefault",
  "organizationOverride",
] as const;
export type PlatformFeeRuleScope = (typeof platformFeeRuleScopes)[number];

export const platformFeePolicySources = [
  "organizationOverride",
  "componentDefault",
  "platformFallback",
  "noFee",
] as const;
export type PlatformFeePolicySource = (typeof platformFeePolicySources)[number];

export type OrganizationPlanCatalogRecord = {
  planId: OrganizationPlanId;
  displayName: string;
  displayNameAr: string | null;
  status: OrganizationPlanStatus;
  billingInterval: "free" | "month" | "year";
  priceMinor: number;
  currency: "usd";
  defaultEntitlements: Record<OrganizationEntitlementId, boolean>;
  defaultLimits: {
    storageBytes: number;
    cloudAiMonthlyActions: number;
    organizationLocations: number;
  };
  defaultPlatformFeePolicyId: string;
  trial: {
    eligible: boolean;
    defaultDays: number;
    targetPlanId: OrganizationPlanId | null;
  };
  version: number;
  source: "experience_thirty";
  createdAt: unknown;
  updatedAt: unknown;
};

export type OrganizationSubscriptionConfig = {
  organizationId: string;
  planId: OrganizationPlanId;
  status: OrganizationSubscriptionStatus;
  billingState: "none" | "pending" | "active" | "pastDue" | "restricted" | "cancelled";
  trialStatus: OrganizationTrialStatus;
  trialTargetPlanId: OrganizationPlanId | null;
  trialStartedAt: unknown | null;
  trialEndsAt: unknown | null;
  foundingStatus: FoundingOrganizationStatus;
  foundingSlotNumber: number | null;
  foundingDiscountBasisPoints: number;
  effectiveSubscriptionDiscountBasisPoints: number;
  effectiveEntitlementsVersion: number;
  source: "experience_thirty";
  createdAt: unknown;
  updatedAt: unknown;
  updatedBy: string;
};

export type OrganizationEntitlementOverride = {
  entitlementId: OrganizationEntitlementId;
  mode: EntitlementOverrideMode;
  organizationId: string;
  reason: string;
  source: "experience_thirty";
  updatedAt: unknown;
  updatedBy: string;
};

export type OrganizationUsageLimitSnapshot = {
  organizationId: string;
  storageBytesUsed: number;
  storageBytesLimit: number;
  storageStatus: UsageLimitStatus;
  cloudAiMonthlyActionsUsed: number;
  cloudAiMonthlyActionsLimit: number;
  organizationLocationsUsed: number;
  organizationLocationsLimit: number;
  canUploadStorage: boolean;
  source: "experience_thirty";
  updatedAt: unknown;
  updatedBy: string;
};

export type PlatformFeePolicy = {
  policyId: string;
  displayName: string;
  effectiveFrom: unknown | null;
  effectiveUntil: unknown | null;
  status: PlatformFeePolicyStatus;
  feeType: PlatformFeeType;
  basisPoints: number;
  fixedMinor: number;
  minimumMinor: number | null;
  maximumMinor: number | null;
  currency: "usd";
  enabled: boolean;
  applicableTransactionTypes: PlatformFeeTransactionType[];
  version: number;
  source: "experience_thirty";
  createdAt: unknown;
  updatedAt: unknown;
  updatedBy: string;
};

export type PlatformFeeRule = {
  ruleId: string;
  displayName: string;
  scope: PlatformFeeRuleScope;
  component: CommerceComponent | null;
  organizationId: string | null;
  status: PlatformFeePolicyStatus;
  feeType: PlatformFeeType;
  basisPoints: number;
  fixedMinor: number;
  minimumMinor: number | null;
  maximumMinor: number | null;
  currency: "usd";
  collectionMode: PlatformFeeCollectionMode;
  enabled: boolean;
  version: number;
  source: "commerce_v1";
  createdAt: unknown;
  updatedAt: unknown;
  updatedBy: string;
};

export type PlatformFeeSnapshot = {
  baseAmountMinor: number;
  platformFeeMinor: number;
  payerTotalMinor: number;
  organizationNetMinor: number;
  currency: "usd";
  component: CommerceComponent;
  collectionMode: PlatformFeeCollectionMode;
  policySource: PlatformFeePolicySource;
  feePolicyId: string | null;
  feePolicyVersion: number | null;
  feeType: PlatformFeeType;
  basisPoints: number;
  fixedMinor: number;
  minimumMinor: number | null;
  maximumMinor: number | null;
};

export type PlatformFeeResolution = PlatformFeeSnapshot & {
  selectedRule: PlatformFeeRule | null;
};

export type FoundingOrganizationSlot = {
  organizationId: string;
  slotNumber: number;
  status: "active" | "removed";
  discountBasisPoints: number;
  qualifiedAt: unknown;
  qualifiedBy: string;
  immutableQualificationReason: string;
  source: "experience_thirty";
  updatedAt: unknown;
};

export type ResolvedEntitlement = {
  entitlementId: OrganizationEntitlementId;
  enabled: boolean;
  source: "plan" | "override" | "restriction";
};

export function isOrganizationPlanId(value: unknown): value is OrganizationPlanId {
  return typeof value === "string" && organizationPlanIds.includes(value as OrganizationPlanId);
}

export function isOrganizationSubscriptionStatus(
  value: unknown,
): value is OrganizationSubscriptionStatus {
  return (
    typeof value === "string" &&
    organizationSubscriptionStatuses.includes(value as OrganizationSubscriptionStatus)
  );
}

export function allEntitlements(defaultValue: boolean): Record<OrganizationEntitlementId, boolean> {
  return Object.fromEntries(
    organizationEntitlementIds.map((entitlementId) => [entitlementId, defaultValue]),
  ) as Record<OrganizationEntitlementId, boolean>;
}

export function validatePlanCatalogRecord(plan: OrganizationPlanCatalogRecord): string[] {
  const errors: string[] = [];

  if (!isOrganizationPlanId(plan.planId)) {
    errors.push("Plan ID must be canonical.");
  }
  if (!organizationPlanStatuses.includes(plan.status)) {
    errors.push("Plan status must be canonical.");
  }
  if (!Number.isSafeInteger(plan.priceMinor) || plan.priceMinor < 0) {
    errors.push("Plan price must be a non-negative integer minor-unit amount.");
  }
  if (plan.currency !== "usd") {
    errors.push("Plan currency must be usd.");
  }
  if (!Number.isSafeInteger(plan.version) || plan.version < 1) {
    errors.push("Plan version must be a positive integer.");
  }
  for (const entitlementId of organizationEntitlementIds) {
    if (typeof plan.defaultEntitlements[entitlementId] !== "boolean") {
      errors.push(`Plan entitlement ${entitlementId} must be boolean.`);
    }
  }
  for (const [name, value] of Object.entries(plan.defaultLimits)) {
    if (!Number.isSafeInteger(value) || value < 0) {
      errors.push(`Plan limit ${name} must be a non-negative integer.`);
    }
  }
  if (
    !Number.isSafeInteger(plan.trial.defaultDays) ||
    plan.trial.defaultDays < 0 ||
    (!plan.trial.eligible && plan.trial.defaultDays !== 0)
  ) {
    errors.push("Plan trial days must match trial eligibility.");
  }
  if (plan.trial.targetPlanId !== null && !isOrganizationPlanId(plan.trial.targetPlanId)) {
    errors.push("Plan trial target must be canonical when present.");
  }

  return errors;
}

export function resolveOrganizationEntitlements(input: {
  plan: OrganizationPlanCatalogRecord;
  subscription: OrganizationSubscriptionConfig;
  overrides?: OrganizationEntitlementOverride[];
}): ResolvedEntitlement[] {
  const overrideByEntitlement = new Map(
    (input.overrides ?? []).map((override) => [override.entitlementId, override]),
  );
  const restricted =
    input.subscription.status === "pastDue" || input.subscription.status === "restricted";

  return organizationEntitlementIds.map((entitlementId) => {
    const override = overrideByEntitlement.get(entitlementId);
    if (override?.mode === "forceEnabled") {
      return { entitlementId, enabled: true, source: "override" };
    }
    if (override?.mode === "forceDisabled") {
      return { entitlementId, enabled: false, source: "override" };
    }
    if (restricted && entitlementId !== "community.core") {
      return { entitlementId, enabled: false, source: "restriction" };
    }

    return {
      entitlementId,
      enabled: input.plan.defaultEntitlements[entitlementId] === true,
      source: "plan",
    };
  });
}

export function calculatePlatformFeeMinor(input: {
  amountMinor: number;
  policy: PlatformFeePolicy;
  transactionType: PlatformFeeTransactionType;
}): number {
  const { amountMinor, policy, transactionType } = input;
  if (
    !policy.enabled ||
    policy.status !== "active" ||
    policy.feeType === "none" ||
    !policy.applicableTransactionTypes.includes(transactionType)
  ) {
    return 0;
  }
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) {
    throw new Error("amount_minor_invalid");
  }

  let feeMinor = 0;
  if (policy.feeType === "basisPoints" || policy.feeType === "basisPointsPlusFixed") {
    feeMinor += Math.floor((amountMinor * policy.basisPoints) / 10_000);
  }
  if (policy.feeType === "fixed" || policy.feeType === "basisPointsPlusFixed") {
    feeMinor += policy.fixedMinor;
  }
  if (policy.minimumMinor !== null) {
    feeMinor = Math.max(feeMinor, policy.minimumMinor);
  }
  if (policy.maximumMinor !== null) {
    feeMinor = Math.min(feeMinor, policy.maximumMinor);
  }

  return Math.min(feeMinor, amountMinor);
}

export function platformFeeGlobalFallbackRuleId(): string {
  return "global-fallback";
}

export function platformFeeComponentDefaultRuleId(component: CommerceComponent): string {
  return `component-default-${component}`;
}

export function platformFeeOrganizationOverrideRuleId(input: {
  component: CommerceComponent;
  organizationId: string;
}): string {
  return `org-${input.organizationId}-${input.component}`;
}

export function commerceComponentToTransactionType(
  component: CommerceComponent,
): PlatformFeeTransactionType {
  return component === "spaceBooking"
    ? "spaceRental"
    : component === "iftarSponsorship"
      ? "ramadanSponsorship"
      : "donation";
}

export function isCommerceComponent(value: unknown): value is CommerceComponent {
  return typeof value === "string" && commerceComponents.includes(value as CommerceComponent);
}

export function isPlatformFeeCollectionMode(value: unknown): value is PlatformFeeCollectionMode {
  return (
    typeof value === "string" &&
    platformFeeCollectionModes.includes(value as PlatformFeeCollectionMode)
  );
}

export function isPlatformFeeRuleScope(value: unknown): value is PlatformFeeRuleScope {
  return typeof value === "string" && platformFeeRuleScopes.includes(value as PlatformFeeRuleScope);
}

export function platformFeePolicySourceFromRuleScope(
  scope: PlatformFeeRuleScope,
): PlatformFeePolicySource {
  return scope;
}

export function validatePlatformFeeRule(rule: PlatformFeeRule): string[] {
  const errors: string[] = [];
  if (!rule.ruleId || rule.ruleId.length > 180) {
    errors.push("Fee rule ID is required.");
  }
  if (!platformFeeRuleScopes.includes(rule.scope)) {
    errors.push("Fee rule scope must be canonical.");
  }
  if (rule.scope === "platformFallback") {
    if (rule.component !== null || rule.organizationId !== null) {
      errors.push("Platform fallback rules cannot be scoped to an organization or component.");
    }
  } else if (rule.scope === "componentDefault") {
    if (!rule.component || rule.organizationId !== null) {
      errors.push("Component defaults require a component and no organization.");
    }
  } else if (!rule.component || !rule.organizationId) {
    errors.push("Organization overrides require both organization and component.");
  }
  if (!platformFeeTypes.includes(rule.feeType)) {
    errors.push("Fee type must be canonical.");
  }
  if (!Number.isSafeInteger(rule.basisPoints) || rule.basisPoints < 0 || rule.basisPoints > 5_000) {
    errors.push("Basis points must be between 0 and 5000.");
  }
  if (!Number.isSafeInteger(rule.fixedMinor) || rule.fixedMinor < 0 || rule.fixedMinor > 100_000) {
    errors.push("Fixed fee must be a non-negative USD minor-unit value.");
  }
  if (
    rule.minimumMinor !== null &&
    (!Number.isSafeInteger(rule.minimumMinor) || rule.minimumMinor < 0)
  ) {
    errors.push("Minimum fee must be null or non-negative.");
  }
  if (
    rule.maximumMinor !== null &&
    (!Number.isSafeInteger(rule.maximumMinor) || rule.maximumMinor < 0)
  ) {
    errors.push("Maximum fee must be null or non-negative.");
  }
  if (
    rule.minimumMinor !== null &&
    rule.maximumMinor !== null &&
    rule.minimumMinor > rule.maximumMinor
  ) {
    errors.push("Minimum fee cannot exceed maximum fee.");
  }
  if (rule.currency !== "usd") {
    errors.push("Fee rule currency must be usd.");
  }
  if (!platformFeeCollectionModes.includes(rule.collectionMode)) {
    errors.push("Fee collection mode must be canonical.");
  }
  if (!Number.isSafeInteger(rule.version) || rule.version < 1) {
    errors.push("Fee rule version must be a positive integer.");
  }
  if (rule.feeType === "none" && (rule.basisPoints !== 0 || rule.fixedMinor !== 0)) {
    errors.push("A none fee rule must not configure percentage or fixed fee values.");
  }
  if (rule.feeType === "basisPoints" && rule.basisPoints === 0 && rule.enabled) {
    errors.push("A percentage fee rule must configure basis points.");
  }
  if (rule.feeType === "fixed" && rule.fixedMinor === 0 && rule.enabled) {
    errors.push("A fixed fee rule must configure a fixed amount.");
  }
  if (
    rule.feeType === "basisPointsPlusFixed" &&
    rule.basisPoints === 0 &&
    rule.fixedMinor === 0 &&
    rule.enabled
  ) {
    errors.push("A combined fee rule must configure percentage, fixed amount, or both.");
  }
  return errors;
}

export function calculatePlatformFeeMinorForRule(input: {
  amountMinor: number;
  rule: Pick<
    PlatformFeeRule,
    | "basisPoints"
    | "enabled"
    | "feeType"
    | "fixedMinor"
    | "maximumMinor"
    | "minimumMinor"
    | "status"
  >;
}): number {
  const { amountMinor, rule } = input;
  if (!rule.enabled || rule.status !== "active" || rule.feeType === "none" || amountMinor <= 0) {
    return 0;
  }
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) {
    throw new Error("amount_minor_invalid");
  }

  let feeMinor = 0;
  if (rule.feeType === "basisPoints" || rule.feeType === "basisPointsPlusFixed") {
    feeMinor += Math.floor((amountMinor * rule.basisPoints) / 10_000);
  }
  if (rule.feeType === "fixed" || rule.feeType === "basisPointsPlusFixed") {
    feeMinor += rule.fixedMinor;
  }
  if (rule.minimumMinor !== null) {
    feeMinor = Math.max(feeMinor, rule.minimumMinor);
  }
  if (rule.maximumMinor !== null) {
    feeMinor = Math.min(feeMinor, rule.maximumMinor);
  }
  return Math.min(feeMinor, amountMinor);
}

export function resolvePlatformFee(input: {
  amountMinor: number;
  component: CommerceComponent;
  componentDefault?: PlatformFeeRule | null;
  organizationOverride?: PlatformFeeRule | null;
  platformFallback?: PlatformFeeRule | null;
}): PlatformFeeResolution {
  const selectedRule =
    activeRuleForComponent(input.organizationOverride, input.component, "organizationOverride") ??
    activeRuleForComponent(input.componentDefault, input.component, "componentDefault") ??
    activeRuleForComponent(input.platformFallback, input.component, "platformFallback");

  if (!selectedRule) {
    return noPlatformFeeResolution(input.amountMinor, input.component);
  }

  const platformFeeMinor = calculatePlatformFeeMinorForRule({
    amountMinor: input.amountMinor,
    rule: selectedRule,
  });
  const payerTotalMinor =
    selectedRule.collectionMode === "addOnToPayer"
      ? input.amountMinor + platformFeeMinor
      : input.amountMinor;
  return {
    baseAmountMinor: input.amountMinor,
    basisPoints: selectedRule.basisPoints,
    collectionMode: selectedRule.collectionMode,
    component: input.component,
    currency: selectedRule.currency,
    feePolicyId: selectedRule.ruleId,
    feePolicyVersion: selectedRule.version,
    feeType: selectedRule.feeType,
    fixedMinor: selectedRule.fixedMinor,
    maximumMinor: selectedRule.maximumMinor,
    minimumMinor: selectedRule.minimumMinor,
    organizationNetMinor: payerTotalMinor - platformFeeMinor,
    payerTotalMinor,
    platformFeeMinor,
    policySource: platformFeePolicySourceFromRuleScope(selectedRule.scope),
    selectedRule,
  };
}

export function noPlatformFeeResolution(
  amountMinor: number,
  component: CommerceComponent,
): PlatformFeeResolution {
  return {
    baseAmountMinor: amountMinor,
    basisPoints: 0,
    collectionMode: "deductFromOrg",
    component,
    currency: "usd",
    feePolicyId: null,
    feePolicyVersion: null,
    feeType: "none",
    fixedMinor: 0,
    maximumMinor: null,
    minimumMinor: null,
    organizationNetMinor: amountMinor,
    payerTotalMinor: amountMinor,
    platformFeeMinor: 0,
    policySource: "noFee",
    selectedRule: null,
  };
}

function activeRuleForComponent(
  rule: PlatformFeeRule | null | undefined,
  component: CommerceComponent,
  scope: PlatformFeeRuleScope,
): PlatformFeeRule | null {
  if (!rule || !rule.enabled || rule.status !== "active" || rule.scope !== scope) {
    return null;
  }
  if (scope !== "platformFallback" && rule.component !== component) {
    return null;
  }
  return validatePlatformFeeRule(rule).length === 0 ? rule : null;
}

export function canUploadStorageForUsage(usage: OrganizationUsageLimitSnapshot): boolean {
  return usage.storageStatus === "underLimit" && usage.canUploadStorage;
}

export function nextFoundingSlotNumber(currentActiveCount: number): number | null {
  if (!Number.isSafeInteger(currentActiveCount) || currentActiveCount < 0) {
    throw new Error("founding_count_invalid");
  }
  const nextSlot = currentActiveCount + 1;
  return nextSlot <= 100 ? nextSlot : null;
}
